import { SystemStyleObject } from '@chakra-ui/react'

import { tokenPrefix } from '../../theme/tokenPrefix'
import type { ColorScheme } from '../../types'
import type { SidePanelSize } from './SidePanel'

// Design Library · Foundation → Side Panel (node 1081:50).
// Sm=400 · Md=480 · Lg=560; bg/card · border/subtle (left edge only);
// sharp four corners — Side Panels do not chamfer.
export const SIDE_PANEL_WIDTHS: Record<SidePanelSize, number> = {
  sm: 400,
  md: 480,
  lg: 560,
}

export const COLLAPSED_WIDTH = 40

// Diameter of the round collapse/expand affordance.
const TOGGLE_DIAMETER = 32

// Inset edge shadow, matching the web-app incident drawer container so docked
// rails read the same in light mode (the `elevated` token is tuned for
// floating dark surfaces and reads as a heavy blur on a white panel).
const EDGE_SHADOW = 'inset -6px 0 15px -6px rgba(0, 0, 0, 0.15)'

// Drawer chrome metrics shared with the incident drawer: 24px horizontal
// gutters, 16px header/footer vertical padding.
const GUTTER = '24px'

export const containerStyles = (
  colorScheme: ColorScheme,
  size: SidePanelSize,
  isExpanded: boolean
): SystemStyleObject => {
  const prefix = tokenPrefix(colorScheme)
  return {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    // Fixed tier width: the rail must not shrink when it shares a flex row.
    flexShrink: 0,
    width: `${isExpanded ? SIDE_PANEL_WIDTHS[size] : COLLAPSED_WIDTH}px`,
    bg: isExpanded ? `${prefix}bg.card` : 'transparent',
    borderLeftWidth: isExpanded ? '1px' : 0,
    borderLeftStyle: 'solid',
    borderColor: `${prefix}border.subtle`,
    boxShadow: isExpanded ? EDGE_SHADOW : 'none',
    borderRadius: 0,
    // No `overflow: hidden` here — the collapse toggle is positioned outside
    // the left edge and must sit on top of it, the way the legacy floor-plan
    // rails do. The content Box below does the clipping instead.
  }
}

// Keeps panel content mounted while collapsed so view state (list vs detail)
// survives a collapse/expand cycle.
export const contentStyles = (isExpanded: boolean): SystemStyleObject => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  width: '100%',
  minHeight: 0,
  overflow: 'hidden',
  visibility: isExpanded ? 'visible' : 'hidden',
})

export const headerStyles = (colorScheme: ColorScheme): SystemStyleObject => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  px: GUTTER,
  // 24 top / 16 bottom: the Figma header is a 64px box around two lines, and a
  // header carrying a title + description + action needs the extra top inset to
  // avoid reading as cramped. Both values are on the Design Library spacing
  // scale (0/4/8/12/16/24/32…) — 20 is not.
  pt: GUTTER,
  pb: '16px',
  width: '100%',
  flexShrink: 0,
  borderBottomWidth: '1px',
  borderBottomStyle: 'solid',
  borderColor: `${tokenPrefix(colorScheme)}border.subtle`,
})

// The single scroll region of the panel. Extra bottom padding keeps the last
// row clear of the footer / viewport edge.
export const bodyStyles = (isPadded: boolean): SystemStyleObject => ({
  display: 'flex',
  flexDirection: 'column',
  flex: '1 1 0%',
  minHeight: 0,
  width: '100%',
  overflowX: 'hidden',
  overflowY: 'auto',
  ...(isPadded ? { px: GUTTER, pt: '16px', pb: '32px' } : {}),
})

export const footerStyles = (colorScheme: ColorScheme): SystemStyleObject => ({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  px: GUTTER,
  py: '16px',
  width: '100%',
  flexShrink: 0,
  borderTopWidth: '1px',
  borderTopStyle: 'solid',
  borderColor: `${tokenPrefix(colorScheme)}border.subtle`,
})

// Collapse affordance floating over the panel's outer-left edge.
export const toggleWrapperStyles: SystemStyleObject = {
  position: 'absolute',
  top: '45px',
  left: 0,
  transform: 'translate(-50%, -50%)',
  // Above the panel surface so the button reads as overlapping the edge.
  zIndex: 2,
}

export const toggleButtonStyles = (
  colorScheme: ColorScheme
): SystemStyleObject => {
  const prefix = tokenPrefix(colorScheme)
  return {
    // UtilityButton's base box is 56×40, which reads as a pill once rounded —
    // square it off so the collapse affordance is a circle like the other rails.
    width: `${TOGGLE_DIAMETER}px`,
    height: `${TOGGLE_DIAMETER}px`,
    minWidth: `${TOGGLE_DIAMETER}px`,
    bg: `${prefix}bg.card`,
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: `${prefix}border.subtle`,
    borderRadius: 'full',
  }
}
